import { Box, Button, Divider, Menu, MenuItem, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../core/contexts/AuthContext';
import { useTenant } from '../../core/contexts/TenantContext';
import { resources } from '../../core/services/resources';
import { NewTenantDialog } from './NewTenantDialog';
import type { Tenant } from '../../core/types/data-models';

interface TenantSwitcherProps {
  fullWidth?: boolean;
}

/**
 * Tenant dropdown — lists every tenant and lets the user flip between
 * them. POST /auth/switch-tenant mints a fresh JWT scoped to the new
 * tenant; the auth context then clears the TanStack-Query cache so the
 * whole UI re-fetches under the new scope.
 */
export function TenantSwitcher({ fullWidth }: TenantSwitcherProps = {}) {
  const { switchTenant } = useAuth();
  const { activeTenantId, activeTenantName } = useTenant();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const tenantsQuery = useQuery({
    queryKey: ['tenants', { switcher: true }],
    queryFn: () => resources.tenants.list({ pageSize: 100, sort: 'name' }),
  });

  const tenants = tenantsQuery.data?.data ?? [];

  const activeName = useMemo(() => {
    if (activeTenantName) return activeTenantName;
    const match = tenants.find((t) => t.id === activeTenantId);
    return match?.name ?? (activeTenantId ? `Tenant #${activeTenantId}` : 'Select tenant');
  }, [activeTenantName, activeTenantId, tenants]);

  const open = Boolean(anchor);

  const handleSelect = async (tenant: Tenant) => {
    setAnchor(null);
    if (tenant.id !== activeTenantId) {
      await switchTenant(tenant.id);
    }
  };

  return (
    <>
      <Button
        size="small"
        fullWidth={fullWidth}
        onClick={(e) => setAnchor(e.currentTarget)}
        endIcon={<ExpandMoreIcon />}
        variant="outlined"
        sx={{
          textTransform: 'none',
          minWidth: 200,
          justifyContent: 'space-between',
          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="body2" fontWeight={600} noWrap>
          {activeName}
        </Typography>
      </Button>

      <Menu
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 260, maxHeight: 420 } } }}
      >
        {tenantsQuery.isLoading && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              Loading tenants…
            </Typography>
          </MenuItem>
        )}
        {!tenantsQuery.isLoading && tenants.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No tenants yet
            </Typography>
          </MenuItem>
        )}
        {tenants.map((t) => {
          const isActive = t.id === activeTenantId;
          return (
            <MenuItem key={t.id} selected={isActive} onClick={() => handleSelect(t)}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Typography variant="body2" fontWeight={isActive ? 600 : 400} sx={{ flexGrow: 1 }}>
                  {t.name}
                </Typography>
                {isActive && <CheckIcon fontSize="small" color="primary" />}
              </Box>
            </MenuItem>
          );
        })}

        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            setCreateOpen(true);
          }}
        >
          <AddIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="body2" color="primary" fontWeight={600}>
            New tenant
          </Typography>
        </MenuItem>
      </Menu>

      <NewTenantDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(tenant) => {
          // Switch into the just-created tenant so the next page load is scoped to it.
          void switchTenant(tenant.id);
        }}
      />
    </>
  );
}
